import React, { useEffect, useRef, useState } from "react";
import { useQuery } from "react-query";
import { useNavigate } from "react-router-dom";
import { HiOutlineSparkles } from "react-icons/hi";
import {
  FiArrowRight,
  FiChevronRight,
  FiRefreshCw,
  FiSend,
  FiX,
} from "react-icons/fi";
import axios from "../../config/axiosInstance";
import { ASSISTANT } from "../../config/apiConfig";
import { useApplication } from "../../contexts/ApplicationContext";
import { collectLinks, crumbsOf, parseBlocks } from "./assistantText";
import AssistantCards from "./AssistantCards";

/* Assistente com IA: bolha no canto e janela de conversa.

   Quem fala é o sistema do "pai" do cliente (o representante, no white-label):
   o nome vem do servidor, a cor é a do tema (--primary). A janela fica aberta
   ao trocar de página — o componente mora no layout. No celular a bolha fica à
   esquerda (à direita estão os botões flutuantes de filtros) e a conversa
   ocupa a tela toda. */

const LABEL_SEEN_KEY = "assistant.labelSeen";
const EXIT_MS = 160;

const readFlag = (key) => {
  try {
    return window.localStorage.getItem(key) === "1";
  } catch {
    return false;
  }
};
const writeFlag = (key) => {
  try {
    window.localStorage.setItem(key, "1");
  } catch {
    /* sem armazenamento: só volta a mostrar o rótulo */
  }
};

const Inline = ({ parts, onLink }) =>
  parts.map((part, index) =>
    part.type === "break" ? (
      <br key={index} />
    ) : part.type === "bold" ? (
      <b key={index}>{part.text}</b>
    ) : part.type === "link" ? (
      // no meio do texto, só a cor do tema; os botões ficam no pé da resposta
      <button
        key={index}
        type="button"
        onClick={() => onLink(part.to)}
        className="font-semibold text-[var(--primary)] underline decoration-slate-300 underline-offset-2 transition hover:decoration-[var(--primary)]"
      >
        {part.text}
      </button>
    ) : (
      <React.Fragment key={index}>{part.text}</React.Fragment>
    ),
  );

// "Relatórios ❯ Consultas →": a tela como caminho, num botão contornado
const GoTo = ({ link, onLink }) => (
  <button
    type="button"
    onClick={() => onLink(link.to)}
    className="group inline-flex max-w-full items-center gap-1 rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-[11.5px] font-semibold text-slate-700 transition hover:border-[var(--primary)] hover:text-[var(--primary)]"
  >
    {crumbsOf(link.text).map((crumb, index, all) => (
      <React.Fragment key={index}>
        {index > 0 && (
          <FiChevronRight
            aria-hidden="true"
            className="shrink-0 text-slate-400"
          />
        )}
        <span
          className={`truncate ${index === all.length - 1 ? "" : "font-medium text-slate-500"}`}
        >
          {crumb}
        </span>
      </React.Fragment>
    ))}
    <FiArrowRight
      aria-hidden="true"
      className="ml-0.5 shrink-0 transition-transform group-hover:translate-x-0.5"
    />
  </button>
);

const AssistantText = ({ text, onLink }) => {
  const blocks = parseBlocks(text);
  const links = collectLinks(blocks);
  return (
    <div className="space-y-1.5">
      {blocks.map((block, index) =>
        block.type === "list" ? (
          <ul key={index} className="list-disc space-y-0.5 pl-4">
            {block.items.map((item, i) => (
              <li key={i}>
                <Inline parts={item} onLink={onLink} />
              </li>
            ))}
          </ul>
        ) : (
          <p key={index}>
            {block.lines.map((line, i) => (
              <React.Fragment key={i}>
                {i > 0 && <br />}
                <Inline parts={line} onLink={onLink} />
              </React.Fragment>
            ))}
          </p>
        ),
      )}
      {links.length > 0 && (
        <div className="mt-2 border-t border-slate-100 pt-2">
          <p className="mb-1.5 text-[10px] font-extrabold uppercase tracking-[0.08em] text-slate-400">
            Ir para
          </p>
          <div className="flex flex-wrap gap-1.5">
            {links.map((link) => (
              <GoTo key={link.to} link={link} onLink={onLink} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const Avatar = ({ size = "h-8 w-8" }) => (
  <span
    className={`grid ${size} shrink-0 place-items-center rounded-full bg-[var(--primary)] text-white`}
  >
    <HiOutlineSparkles aria-hidden="true" />
  </span>
);

const AssistantBubble = () => {
  const { user, userPermission } = useApplication();
  const navigate = useNavigate();
  const allowed = !!user?.id && userPermission !== "PROVIDER";

  const { data: info } = useQuery(
    ["assistantBootstrap", user?.id],
    async () => {
      const { data } = await axios.get(ASSISTANT.BOOTSTRAP);
      return data?.data || null;
    },
    { enabled: allowed, staleTime: Infinity, retry: false },
  );

  const [open, setOpen] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [visible, setVisible] = useState(false);
  const [messages, setMessages] = useState(null);
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const [showLabel, setShowLabel] = useState(() => !readFlag(LABEL_SEEN_KEY));
  const listRef = useRef(null);
  const inputRef = useRef(null);

  // abre deslizando e some suave; desmonta depois da animação
  useEffect(() => {
    if (open) {
      setMounted(true);
      let inner;
      const outer = requestAnimationFrame(() => {
        inner = requestAnimationFrame(() => setVisible(true));
      });
      return () => {
        cancelAnimationFrame(outer);
        cancelAnimationFrame(inner);
      };
    }
    setVisible(false);
    const timer = setTimeout(() => setMounted(false), EXIT_MS);
    return () => clearTimeout(timer);
  }, [open]);

  // conversa salva: carrega na primeira abertura
  useEffect(() => {
    if (!open || messages) return;
    let cancelled = false;
    axios
      .get(ASSISTANT.HISTORY)
      .then(
        ({ data }) =>
          !cancelled && setMessages(Array.isArray(data?.data) ? data.data : []),
      )
      .catch(() => !cancelled && setMessages([]));
    return () => {
      cancelled = true;
    };
  }, [open, messages]);

  // o campo só existe depois que a janela monta
  useEffect(() => {
    if (open && visible) inputRef.current?.focus({ preventScroll: true });
  }, [open, visible]);

  // depois do próximo quadro: os cartões da resposta já entraram na altura
  useEffect(() => {
    const frame = requestAnimationFrame(() => {
      const list = listRef.current;
      if (list) list.scrollTop = list.scrollHeight;
    });
    return () => cancelAnimationFrame(frame);
  }, [messages, sending, error, visible]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (event) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open]);

  if (!allowed || !info?.enabled) return null;

  const openPanel = () => {
    setOpen(true);
    if (showLabel) {
      setShowLabel(false);
      writeFlag(LABEL_SEEN_KEY);
    }
  };

  const send = async (text) => {
    const message = String(text ?? draft).trim();
    if (!message || sending) return;
    setDraft("");
    if (inputRef.current) inputRef.current.style.height = "";
    setError("");
    setMessages((current) => [
      ...(current || []),
      { id: `me-${Date.now()}`, role: "user", content: message },
    ]);
    setSending(true);
    try {
      const { data } = await axios.post(ASSISTANT.CHAT, { message });
      setMessages((current) => [...(current || []), data.data]);
    } catch (err) {
      setError(
        err?.response?.data?.message ||
          "Não consegui responder agora. Tente de novo em instantes.",
      );
    } finally {
      setSending(false);
      inputRef.current?.focus({ preventScroll: true });
    }
  };

  const newConversation = async () => {
    setMessages([]);
    setError("");
    try {
      await axios.delete(ASSISTANT.HISTORY);
    } catch {
      /* a conversa antiga continua guardada; a tela já começou outra */
    }
  };

  const goTo = (path) => {
    navigate(path);
    // no celular a conversa cobre a tela: fecha para mostrar a página
    if (window.matchMedia?.("(max-width: 767px)").matches) setOpen(false);
  };

  const empty = (messages || []).length === 0;

  /* Uma linha tem a altura do botão de enviar (texto no meio); mais linhas
     crescem para cima até 7. */
  const fitInput = (element) => {
    if (!element) return;
    element.style.height = "auto";
    element.style.height = `${Math.min(element.scrollHeight, 112)}px`;
  };

  return (
    <>
      {!open && (
        <div className="fixed bottom-4 left-4 z-40 flex items-center gap-2 md:bottom-5 md:left-auto md:right-5 md:flex-row-reverse">
          <button
            type="button"
            onClick={openPanel}
            aria-label={`Abrir ${info.assistantName}`}
            className="grid h-14 w-14 place-items-center rounded-full bg-[var(--primary)] text-2xl text-white shadow-2xl transition hover:scale-105 focus:outline-none focus-visible:ring-4 focus-visible:ring-slate-300"
          >
            <HiOutlineSparkles aria-hidden="true" />
          </button>
          {showLabel && (
            <button
              type="button"
              onClick={openPanel}
              className="hidden rounded-full bg-white px-3 py-1.5 text-[12px] font-semibold text-slate-700 shadow-lg ring-1 ring-slate-200 md:block"
            >
              Dúvidas? Central de ajuda
            </button>
          )}
        </div>
      )}

      {mounted && (
        <section
          role="dialog"
          aria-label={info.assistantName}
          className={`fixed inset-0 z-40 flex flex-col overflow-hidden bg-slate-50 shadow-2xl transition duration-150 ease-out motion-reduce:transition-none md:inset-auto md:bottom-5 md:right-5 md:h-[min(620px,calc(100vh-40px))] md:w-[400px] md:origin-bottom-right md:rounded-2xl md:border md:border-slate-200 ${
            visible
              ? "translate-y-0 scale-100 opacity-100"
              : "pointer-events-none translate-y-2 scale-[0.98] opacity-0"
          }`}
        >
          <header className="flex items-center gap-2.5 border-b border-slate-200 bg-white px-4 py-3">
            <Avatar />
            <div className="min-w-0 flex-1">
              <b className="block truncate text-[13px] text-slate-900">
                {info.assistantName}
              </b>
            </div>
            <button
              type="button"
              onClick={newConversation}
              disabled={sending || empty}
              title="Começar uma conversa nova"
              aria-label="Começar uma conversa nova"
              className="grid h-8 w-8 place-items-center rounded-lg text-slate-400 transition hover:bg-slate-100 hover:text-slate-700 disabled:opacity-40"
            >
              <FiRefreshCw aria-hidden="true" />
            </button>
            <button
              type="button"
              onClick={() => setOpen(false)}
              aria-label="Minimizar a Central de ajuda"
              className="grid h-8 w-8 place-items-center rounded-lg text-lg text-slate-400 transition hover:bg-slate-100 hover:text-slate-700"
            >
              <FiX aria-hidden="true" />
            </button>
          </header>

          <div
            ref={listRef}
            className="flex flex-1 flex-col gap-2.5 overflow-y-auto overscroll-contain p-3 text-[12.5px] leading-relaxed"
            aria-live="polite"
          >
            <div className="max-w-[88%] rounded-2xl rounded-bl-md border border-slate-200 bg-white px-3 py-2 text-slate-800">
              {info.greeting}
            </div>
            {messages == null && (
              <div className="h-10 w-2/3 animate-pulse rounded-2xl bg-slate-200/70" />
            )}
            {(messages || []).map((item) =>
              item.role === "user" ? (
                <div
                  key={item.id}
                  className="ml-auto max-w-[82%] whitespace-pre-wrap rounded-2xl rounded-br-md bg-[var(--primary)] px-3 py-2 text-white"
                >
                  {item.content}
                </div>
              ) : (
                <div key={item.id} className="space-y-2">
                  {item.content && (
                    <div className="max-w-[88%] rounded-2xl rounded-bl-md border border-slate-200 bg-white px-3 py-2 text-slate-800">
                      <AssistantText text={item.content} onLink={goTo} />
                    </div>
                  )}
                  {/* listas (consultas, catálogo) em cartões, na largura toda */}
                  <AssistantCards
                    attachments={item.attachments}
                    onLink={goTo}
                  />
                </div>
              ),
            )}
            {sending && (
              <div
                className="flex w-14 items-center justify-center gap-1 rounded-2xl rounded-bl-md border border-slate-200 bg-white px-3 py-3"
                aria-label="Escrevendo resposta"
              >
                {[0, 1, 2].map((dot) => (
                  <span
                    key={dot}
                    className="h-1.5 w-1.5 animate-bounce rounded-full bg-slate-400"
                    style={{ animationDelay: `${dot * 120}ms` }}
                  />
                ))}
              </div>
            )}
            {error && (
              <p className="rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-[12px] text-red-800">
                {error}
              </p>
            )}
          </div>

          <form
            className="border-t border-slate-200 bg-white p-3"
            onSubmit={(event) => {
              event.preventDefault();
              send();
            }}
          >
            {empty && !sending && (
              <div className="mb-2 flex flex-wrap gap-1.5">
                {(info.suggestions || []).map((suggestion) => (
                  <button
                    key={suggestion}
                    type="button"
                    onClick={() => send(suggestion)}
                    className="rounded-full border border-slate-200 bg-white px-2.5 py-1 text-[11.5px] font-semibold text-slate-600 transition hover:border-slate-300 hover:text-slate-900"
                  >
                    {suggestion}
                  </button>
                ))}
              </div>
            )}
            <div className="flex items-end gap-2 rounded-xl border border-slate-300 py-1 pl-3 pr-1 focus-within:border-[var(--primary)]">
              <textarea
                ref={inputRef}
                rows={1}
                value={draft}
                maxLength={1000}
                onChange={(event) => {
                  setDraft(event.target.value);
                  fitInput(event.target);
                }}
                onKeyDown={(event) => {
                  if (event.key === "Enter" && !event.shiftKey) {
                    event.preventDefault();
                    send();
                  }
                }}
                placeholder="Pergunte sobre consultas ou o sistema…"
                aria-label="Sua pergunta"
                className="h-8 max-h-28 flex-1 resize-none border-0 bg-transparent px-0 py-1.5 text-[12.5px] leading-5 text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-0"
              />
              <button
                type="submit"
                disabled={!draft.trim() || sending}
                aria-label="Enviar"
                className="grid h-8 w-8 shrink-0 place-items-center rounded-lg bg-[var(--primary)] text-white transition hover:opacity-90 disabled:opacity-40"
              >
                <FiSend aria-hidden="true" />
              </button>
            </div>
          </form>
        </section>
      )}
    </>
  );
};

export default AssistantBubble;
